function About() {
  return (
    <div className="page about">
      <section className="page-hero">
        <h1>Engineering with clarity, reliability, and measurable impact</h1>
        <p>Vision Forte builds software that launches fast, scales smoothly, and stays secure.</p>
      </section>

      <section className="section">
        <h2>Our Principles</h2>
        <div className="cards">
          <div className="card">
            <h3>Clarity First</h3>
            <p>No jargon, no surprises. We keep scope, timelines, and outcomes crystal clear from day one.</p>
            <ul>
              <li>Plain English communication</li>
              <li>Transparent progress tracking</li>
              <li>Clear success metrics</li>
            </ul>
          </div>
          <div className="card">
            <h3>Quality by Default</h3>
            <p>Reviews, tests, and performance checks built into every sprint—not bolted on later.</p>
            <ul>
              <li>Code reviews and pair programming</li>
              <li>Automated testing and CI/CD</li>
              <li>Performance and security audits</li>
            </ul>
          </div>
          <div className="card">
            <h3>Ownership</h3>
            <p>We treat your product like ours—shipping, monitoring, and improving continuously.</p>
            <ul>
              <li>End-to-end responsibility</li>
              <li>Proactive monitoring and alerts</li>
              <li>Continuous improvement mindset</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section">
        <h2>Expertise</h2>
        <p>
          We work with modern, battle-tested technologies that scale. From frontend frameworks to 
          cloud infrastructure, we choose tools that deliver results and stand the test of time.
        </p>
        <div className="expertise-grid">
          <div className="expertise-item">
            <h4>Frontend & UX</h4>
            <p>React, TypeScript, Next.js, responsive design, and performance optimization</p>
          </div>
          <div className="expertise-item">
            <h4>Backend & APIs</h4>
            <p>Node.js, Express, REST/GraphQL, microservices, and database design</p>
          </div>
          <div className="expertise-item">
            <h4>Cloud & DevOps</h4>
            <p>AWS, Azure, Docker, CI/CD pipelines, monitoring, and infrastructure as code</p>
          </div>
          <div className="expertise-item">
            <h4>Security & Quality</h4>
            <p>Authentication, authorization, secure coding practices, and automated testing</p>
          </div>
        </div>
      </section>

      <section className="section">
        <h2>Our Mission</h2>
        <p>
          We believe great software starts with understanding real problems and ends with solutions 
          that people actually use and love. Our mission is to bridge the gap between ambitious ideas 
          and reliable, scalable products.
        </p>
        <div className="mission-points">
          <div className="mission-point">
            <h4>🚀 Launch Fast</h4>
            <p>Get your MVP in users' hands quickly with iterative development and smart prioritization.</p>
          </div>
          <div className="mission-point">
            <h4>🏗️ Build to Last</h4>
            <p>Architecture and code quality that scales with your business, not against it.</p>
          </div>
          <div className="mission-point">
            <h4>🔄 Evolve Continuously</h4>
            <p>Software that grows and adapts with your needs, not legacy systems that hold you back.</p>
          </div>
        </div>
      </section>

      <section className="section process">
        <h2>How we deliver</h2>
        <ol className="steps timeline">
          <li>
            <div className="step-dot"></div>
            <h4>Partner</h4>
            <p>We listen first and align on goals, constraints, and measures of success.</p>
          </li>
          <li>
            <div className="step-dot"></div>
            <h4>Plan</h4>
            <p>Roadmaps with clear milestones, estimates, and risk management.</p>
          </li>
          <li>
            <div className="step-dot"></div>
            <h4>Build</h4>
            <p>Iterative releases with demos, feedback, and quality gates.</p>
          </li>
          <li>
            <div className="step-dot"></div>
            <h4>Support</h4>
            <p>Monitoring, documentation, and knowledge transfer to your team.</p>
          </li>
        </ol>
      </section>

      <section className="section highlight">
        <h2>Who we help</h2>
        <p>We partner with ambitious teams who want to build something meaningful, not just functional.</p>
        <div className="cards">
          <div className="card help-card">
            <h3>🚀 Startup Founders</h3>
            <p>Validating ideas and launching MVPs with tight budgets and tighter timelines.</p>
            <ul>
              <li>Rapid prototyping and user testing</li>
              <li>Lean development methodologies</li>
              <li>Investor-ready technical foundations</li>
            </ul>
          </div>
          <div className="card help-card">
            <h3>📈 Growing Teams</h3>
            <p>Scaling products and infrastructure as your user base and feature set expands.</p>
            <ul>
              <li>Performance optimization and monitoring</li>
              <li>Microservices and cloud architecture</li>
              <li>Team scaling and knowledge transfer</li>
            </ul>
          </div>
          <div className="card help-card">
            <h3>🏢 Established Businesses</h3>
            <p>Modernizing legacy systems and building new capabilities without disrupting operations.</p>
            <ul>
              <li>Legacy system integration and migration</li>
              <li>Security and compliance upgrades</li>
              <li>Digital transformation strategies</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section cta">
        <h2>Let’s build something dependable</h2>
        <p>We’ll help plan milestones and pick the simplest approach that works.</p>
        <a className="cta-btn" href="/contact">Talk to us</a>
      </section>
    </div>
  );
}

export default About;


